import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';
import { NavController } from '@ionic/angular/standalone';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AuthService } from '../../../service/auth.service';
import { SasTokenService } from '../../../service/sas-token.service';
import * as AuthActions from '../actions/auth.actions';
import * as UserActions from '../../user/actions/user.actions';
import * as ErrorActions from '../../error/actions/error.actions';
import * as NotificationActions from '../../notification/actions/notification.actions';
import { Notification } from '../../notification/actions/notification.actions';
import { selectAuthState } from '../selectors/auth.selectors';

@Injectable()
export class AuthEffects {
  private readonly checkAuth$;
  private readonly handleLoginSuccess$;
  private readonly handleLoginFailure$;
  private readonly navigateOnAuthSuccess$;
  private readonly redirectOnFailureOrLogout$;
  private readonly logout$;
  private readonly forceLogout$;
  private readonly clearUserOnLogout$;

  private actions$ = inject(Actions);
  private authService = inject(AuthService);
  private sasTokenService = inject(SasTokenService);
  private navCtrl = inject(NavController);
  private router = inject(Router);
  private store = inject(Store);

  constructor() {
    // Check authentication status on app initialization
    // Validates auth state rehydrated from localStorage
    this.checkAuth$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AuthActions.checkAuth),
        withLatestFrom(this.store.select(selectAuthState)),
        map(([_, authState]) => {
          // Auth is already rehydrated from localStorage by meta-reducer
          // Validate token and dispatch success/failure
          if (authState.token && authState.isAuthenticated) {
            return AuthActions.checkAuthSuccess({ token: authState.token });
          } else {
            return AuthActions.checkAuthFailure();
          }
        }),
      ),
    );

    // Handle login success by dispatching to both auth and user stores
    this.handleLoginSuccess$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AuthActions.loginSuccess),
        mergeMap(({ token, user }) => [
          AuthActions.checkAuthSuccess({ token }),
          UserActions.setUser({ user }),
        ]),
      ),
    );

    // Navigate after successful login (not on app initialization).
    // ADMIN and SUPER_ADMIN land on /dashboard (Home is visible to them).
    // Everyone else lands on /contacts since the Home menu item is hidden
    // and the dashboard would otherwise show a page they cannot return to
    // from the menu.
    this.navigateOnAuthSuccess$ = createEffect(
      () =>
        this.actions$.pipe(
          ofType(AuthActions.loginSuccess),
          tap(({ user }) => {
            const roles = user?.roles ?? [];
            const isAdminOrAbove =
              roles.includes('ROLE_SUPER_ADMIN') || roles.includes('ROLE_ADMIN');
            const target = isAdminOrAbove ? '/dashboard' : '/contacts';
            void this.navCtrl.navigateRoot(target, { animated: false });
          }),
        ),
      { dispatch: false },
    );

    // Handle login failure by dispatching to both auth and error stores
    this.handleLoginFailure$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AuthActions.loginFailure),
        mergeMap(({ error, errorMessage }) => [
          AuthActions.checkAuthFailure(),
          ErrorActions.GlobalError({
            error: {
              statusCode: 401,
              message: errorMessage,
              timestamp: new Date(),
              actionType: 'Login',
              details: error,
            },
          }),
        ]),
      ),
    );

    this.logout$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AuthActions.logout),
        mergeMap(() =>
          this.authService.logout().pipe(
            map(() => {
              // Successful logout - show info notification
              const notification: Notification = {
                id: Date.now().toString(),
                severity: 'info',
                title: 'Logged Out',
                summary: 'You have been logged out successfully.',
                timestamp: new Date(),
                duration: 3000,
              };

              return [
                NotificationActions.ShowNotification({ notification }),
                AuthActions.logoutSuccess(),
              ];
            }),
            catchError((error) => {
              // Note: 401 errors are handled by the interceptor via forceLogout
              // This handles other errors (network, timeout, 500, etc.)

              // Service already cleared local state, just redirect to login
              console.error(`Logout failed but local state cleared, redirecting to login`, error);

              // Show warning notification instead of success
              const notification: Notification = {
                id: Date.now().toString(),
                severity: 'warning',
                title: 'Logout Warning',
                summary: 'You have been logged out locally.',
                detail:
                  'The server logout request failed, but your local session has been cleared. You are being redirected to the login page.',
                timestamp: new Date(),
                duration: 0, // Don't auto-dismiss warnings
              };

              return of([
                NotificationActions.ShowNotification({ notification }),
                AuthActions.logoutSuccess(),
              ]);
            }),
          ),
        ),
        mergeMap((actions) => actions),
      ),
    );

    // Force logout without API call - handles catastrophic auth failures
    // Used when backend is unreachable, returns 401, or during database loss
    this.forceLogout$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AuthActions.forceLogout),
        tap(({ reason }) => {
          console.warn(`[Force Logout] ${reason || 'No reason provided'}`);
          // The auth slice is no longer persisted (token lives in memory + HttpOnly cookie),
          // so there is nothing to remove from localStorage. Clear the SAS token cache.
          this.sasTokenService.clearCache();
        }),
        mergeMap(({ reason }) => {
          // Show critical warning to user
          const notification: Notification = {
            id: Date.now().toString(),
            severity: 'error',
            title: 'Session Invalid',
            summary: 'Your session is no longer valid.',
            detail:
              reason ||
              'For security reasons, you have been logged out. Please log in again to continue.',
            timestamp: new Date(),
            duration: 0, // Don't auto-dismiss - user needs to see this
          };

          return [
            NotificationActions.ShowNotification({ notification }),
            UserActions.clearUser(),
            AuthActions.logoutSuccess(),
          ];
        }),
      ),
    );

    // Clear user store and SAS token cache on logout success OR when an auth check fails.
    //
    // The `user` slice is persisted to localStorage but the access token is memory-only. On a cold
    // load with no valid session, the rehydrated `user` makes LoginGuard/AuthGuard believe the user
    // is authenticated (→ /dashboard) while AdminGuard sees no access token (→ /login), which on a
    // protected landing route produces an infinite /login ⇄ /dashboard redirect loop. Clearing
    // `user` on checkAuthFailure keeps the two sources of truth consistent and breaks the loop.
    this.clearUserOnLogout$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AuthActions.logoutSuccess, AuthActions.checkAuthFailure),
        tap(() => {
          // Clear SAS token cache on logout for security
          this.sasTokenService.clearCache();
        }),
        map(() => UserActions.clearUser()),
      ),
    );

    this.redirectOnFailureOrLogout$ = createEffect(
      () =>
        this.actions$.pipe(
          ofType(AuthActions.checkAuthFailure, AuthActions.logoutSuccess),
          tap(() => void this.navCtrl.navigateRoot('/login', { animated: false })),
        ),
      { dispatch: false },
    );
  }
}
